import React, { useEffect, useRef, useState } from "react";
import { api, formatDateFR, API } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileText, Eye, AlertTriangle } from "lucide-react";
import { confirmAction } from "../ConfirmDialog";

const DOC_TYPES = [
  { value: "driver_license", label: "Permis de conduire" },
  { value: "id_card", label: "Pièce d'identité" },
  { value: "passport", label: "Passeport" },
  { value: "proof_of_address", label: "Justificatif de domicile" },
  { value: "other", label: "Autre" },
];

export default function ClientDocuments({ clientEmail }) {
  const [docs, setDocs] = useState(null);
  const [docType, setDocType] = useState("driver_license");
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get(`/vendor/clients/${encodeURIComponent(clientEmail)}/documents`)
      .then((r) => setDocs(r.data.documents || []))
      .catch(() => setDocs([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientEmail]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({ doc_type: docType });
      if (expiry) qs.set("expiry_date", expiry);
      await api.post(`/vendor/clients/${encodeURIComponent(clientEmail)}/documents?${qs}`, fd);
      toast.success("Document ajouté");
      setExpiry("");
      load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (docId) => {
    if (!(await confirmAction({
      title: "Supprimer ce document ?",
      message: "Le document sera définitivement supprimé du dossier client.",
      confirmLabel: "Supprimer le document",
      variant: "danger",
    }))) return;
    await api.delete(`/vendor/clients/${encodeURIComponent(clientEmail)}/documents/${docId}`);
    load();
  };

  const view = async (doc) => {
    try {
      const res = await fetch(`${API}/vendor/client-documents/${doc.path}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("cm_token")}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  if (docs === null) return <Loader2 className="h-4 w-4 animate-spin text-violet-600" />;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-2" data-testid="client-documents">
      <div className="flex flex-wrap items-center gap-1.5">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 outline-none focus:ring-1 focus:ring-violet-500" data-testid="client-doc-type">
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} title="Date d'expiration (optionnel)" className="text-xs rounded-lg border border-slate-200 px-2 py-1.5 outline-none focus:ring-1 focus:ring-violet-500" data-testid="client-doc-expiry" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
          data-testid="client-doc-upload-btn"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Ajouter
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="client-doc-file-input" />
      </div>

      {docs.length === 0 ? (
        <p className="text-xs text-slate-400">Aucun document.</p>
      ) : (
        docs.map((d) => {
          const expired = d.expiry_date && d.expiry_date < today;
          return (
            <div key={d.id} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5" data-testid={`client-doc-${d.id}`}>
              <FileText className="h-4 w-4 text-violet-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-slate-900 truncate">{d.label}</div>
                <div className="text-[10px] text-slate-400">
                  {DOC_TYPES.find((t) => t.value === d.type)?.label || d.type}
                  {d.expiry_date && (
                    <span className={expired ? "text-red-600 font-semibold" : "text-amber-600"}>
                      {" "}· {expired ? "Expiré le" : "Expire le"} {formatDateFR(d.expiry_date)}
                    </span>
                  )}
                </div>
              </div>
              {expired && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
              <button onClick={() => view(d)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" title="Voir" data-testid={`client-doc-view-${d.id}`}>
                <Eye className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => remove(d.id)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center" title="Supprimer" data-testid={`client-doc-delete-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}
